import CONFIG from "config/config";
import { FORMATS } from "../constants";
import { TemplateConfig } from "../drawer/types";
import { mapPublishedFormat } from "./mapPublishedFormat";
import { FormatChannel, PublishedFormat, RuntimeFormatConfig } from "./types";

export type PublishedFormatsFetcher = (url: string) => Promise<{ formats: PublishedFormat[] }>;

interface FormatsQuery {
  countryId: string | number;
  channel: FormatChannel;
  fetcher?: PublishedFormatsFetcher;
  // Skip the cache: template URLs are short-lived pre-signed links, so pages that offer downloads re-request them.
  fresh?: boolean;
}

// off: nothing requested; loading: request in flight; ready: API formats served; fallback: API empty/failed.
export type FormatsStatus = "off" | "loading" | "ready" | "fallback";

export interface FormatsState {
  formats: RuntimeFormatConfig[] | null;
  status: FormatsStatus;
  // The (country, channel) the formats belong to, so a campaign change never shows the previous catalogue.
  key: string | null;
}

// Template URLs in /published are pre-signed for 1 hour, so cached answers live for half of that.
const CACHE_TTL_MS = 30 * 60 * 1000;

const cache = new Map<string, { request: Promise<PublishedFormat[]>; at: number }>();
const listeners = new Set<() => void>();
let state: FormatsState = { formats: null, status: "off", key: null };
let loadToken = 0;

const defaultFetcher: PublishedFormatsFetcher = async (url) => {
  // Lazy import keeps the auth/localStorage client out of pure unit tests.
  const { apiClient } = await import("module/api");
  const response = await fetch(apiClient.prepareRequest(url, "GET"));
  if (!response.ok) throw new Error(`GET ${url} failed with ${response.status}`);
  return response.json();
};

export const clearFormatsCache = (): void => cache.clear();

export function fetchPublishedFormats({
  countryId,
  channel,
  fetcher = defaultFetcher,
  fresh = false,
}: FormatsQuery): Promise<PublishedFormat[]> {
  const key = `${countryId}:${channel}`;
  const cached = fresh ? undefined : cache.get(key);
  if (cached && Date.now() - cached.at <= CACHE_TTL_MS) return cached.request;

  // The print-formats routes are served from the API root; API_BASE_URL carries a /platform prefix that 404s.
  const url = `${CONFIG.API_URL}/print-formats/published?countryId=${encodeURIComponent(
    String(countryId)
  )}&channel=${channel}`;
  const request = fetcher(url).then((body) => body.formats ?? []);
  cache.set(key, { request, at: Date.now() });
  request.catch(() => cache.delete(key));
  return request;
}

const setState = (next: FormatsState): void => {
  if (next.formats === state.formats && next.status === state.status && next.key === state.key) return;
  state = next;
  listeners.forEach((listener) => listener());
};

export const resetRuntimeFormats = (): void => {
  loadToken += 1;
  setState({ formats: null, status: "off", key: null });
};

// Referentially stable between changes, as useSyncExternalStore requires.
export const getFormatsState = (): FormatsState => state;

export const getRuntimeFormats = (): RuntimeFormatConfig[] | null => state.formats;

export const subscribeRuntimeFormats = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

// Flag off, API error or an empty answer all leave the registry empty, which means today's hardcoded behaviour.
export async function loadRuntimeFormats(options: FormatsQuery & { enabled: boolean }): Promise<void> {
  if (!options.enabled) {
    resetRuntimeFormats();
    return;
  }

  loadToken += 1;
  const token = loadToken;
  const key = `${options.countryId}:${options.channel}`;
  // Same campaign: keep serving the loaded list while it refreshes. Different one: drop it rather than show another catalogue.
  setState({ formats: state.key === key ? state.formats : null, status: "loading", key });
  try {
    const published = await fetchPublishedFormats(options);
    if (token !== loadToken) return;
    if (published.length === 0) {
      setState({ formats: null, status: "fallback", key });
      return;
    }
    const formats = published.map((format) => mapPublishedFormat(format, FORMATS[format.code]));
    setState({ formats, status: "ready", key });
  } catch (error) {
    if (token !== loadToken) return;
    console.warn("[formats] could not load published formats, using the built-in list", error);
    setState({ formats: null, status: "fallback", key });
  }
}

// The one lookup call sites use: API-backed config when loaded, else the hardcoded FORMATS entry.
export function getEffectiveFormat(code?: string | null): TemplateConfig | RuntimeFormatConfig | undefined {
  if (!code) return undefined;
  return state.formats?.find((format) => format.format === code) ?? FORMATS[code];
}

export const isRuntimeFormat = (config?: TemplateConfig | null): config is RuntimeFormatConfig =>
  !!config && "panelWidthsMm" in config;

// Codes with no hardcoded entry: their templates are generated by the API and may still be pending.
export const isCatalogueOnlyFormat = (config?: TemplateConfig | null): boolean =>
  isRuntimeFormat(config) && config.perSideBleed;
